import { useState, useCallback, useEffect } from "react";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import payrollReasonCodeService from "../services/payrollReasonCodeService";

export const usePayrollReasonCode = ({ showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const fetchReasonCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollReasonCodeService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch payroll reason codes", err);
      showGeneralMessage("Failed to load reason codes", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const fetchActiveReasonCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollReasonCodeService.getAllActive();
      const responseData = res.data || [];
      setActiveData(responseData);
    } catch (err) {
      console.error("Failed to fetch payroll reason codes", err);
      showGeneralMessage("Failed to load reason codes", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const fetchReasonById = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollReasonCodeService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch payroll reason codes", err);
      showGeneralMessage("Failed to load reason codes", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchReasonCodes();
  }, [fetchReasonCodes]);

  const getReasonCodeById = async (id) => {
    const startTime = Date.now();
    setLoading(true);
    try {
      const res = await payrollReasonCodeService.getById(id);

      const elapsed = Date.now() - startTime;
      if (elapsed < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsed));
      }

      return { success: true, data: res.data };
    } catch (err) {
      console.error("Failed to fetch reason code details", err);
      showGeneralMessage("Failed to load reason code details", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);

      if (Array.isArray(auditData)) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData) {
        await SetAuditTrail(auditData);
      }

      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchReasonCodes();
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.description?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower)
    ));
  };

  return {
    loading,
    data,
    activeData,
    searchData,
    getReasonCodeById,
    createReasonCode: (p, audit) => handleAction(payrollReasonCodeService.create, audit, p),
    updateReasonCode: (id, p, audit) => handleAction(payrollReasonCodeService.update, audit, id, p),
    refresh: fetchReasonCodes,
    fetchActiveReasonCodes
  };
};